import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const HUSKS_SIZE_FILTER_EVENT = 'husks:size-filter-change';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener(HUSKS_SIZE_FILTER_EVENT, this.#handleCompositeSizeChange);
    this.addEventListener('click', this.#handleClearAllClick);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
    this.#observeSizeStockCards();
    if (!this.#migrateNativeSizeFilter()) this.#refreshCompositeSizeFilter();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener(HUSKS_SIZE_FILTER_EVENT, this.#handleCompositeSizeChange);
    this.removeEventListener('click', this.#handleClearAllClick);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
    this.#sizeStockObserver?.disconnect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeSizeStockCards();
    this.#refreshCompositeSizeFilter();
  }

  /** @type {MutationObserver | undefined} */
  #sizeStockObserver;

  /** @type {Promise<void> | undefined} */
  #allSizePagesPromise;

  /** @type {string | undefined} */
  #allSizePagesKey;

  /** @type {Promise<void> | undefined} */
  #sizePagesPrefetchPromise;

  /** @type {string | undefined} */
  #sizePagesPrefetchKey;

  #isAppendingSizePages = false;

  #compositePage = 1;

  #isResettingCompositeFilter = false;

  #observeSizeStockCards() {
    this.#sizeStockObserver?.disconnect();

    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!grid) return;

    this.#sizeStockObserver = new MutationObserver(() => {
      if (!this.#isAppendingSizePages) this.#applyCompositeSizeFilter();
    });
    this.#sizeStockObserver.observe(grid, { childList: true });
  }

  #handleCompositeSizeChange = (event) => {
    const values = Array.isArray(event.detail?.values) ? [...new Set(event.detail.values.filter(Boolean))] : [];
    this.#setSelectedSizes(values);
    this.#syncSizeInputs(values);
    this.#compositePage = 1;
    if (values.length === 0) {
      this.#resetCompositeSizeFilter();
      return;
    }
    this.#refreshCompositeSizeFilter();
  };

  #handleClearAllClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('summary');
    if (summary?.parentElement?.querySelector('[data-husks-size-filter]')) {
      this.#prefetchCompositeSizePages();
      return;
    }

    const compositePageLink = event.target.closest('[data-composite-size-page]');
    if (compositePageLink instanceof HTMLAnchorElement) {
      event.preventDefault();
      const page = Number(compositePageLink.dataset.compositeSizePage);
      if (!Number.isFinite(page) || page < 1) return;
      this.#compositePage = page;
      this.#applyCompositeSizeFilter();
      this.querySelector('[data-testid="product-grid"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (!event.target.closest('.facets__clear-all-link, .facets__clear-all')) return;

    this.#setSelectedSizes([]);
    this.#syncSizeInputs([]);
    this.#compositePage = 1;
    this.#resetCompositeSizeFilter();
  };

  #handleCollectionUpdate = (event) => {
    const eventSection = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    const mySection = this.closest('.shopify-section');
    if (eventSection && mySection && eventSection !== mySection) return;

    Promise.resolve(event.promise)
      .then(() => {
        if (!this.isConnected) return;
        this.#compositePage = 1;
        this.#allSizePagesKey = undefined;
        this.#allSizePagesPromise = undefined;
        this.#sizePagesPrefetchKey = undefined;
        this.#sizePagesPrefetchPromise = undefined;
        this.#observeSizeStockCards();
        this.#refreshCompositeSizeFilter();
      })
      .catch(() => {});
  };

  async #resetCompositeSizeFilter() {
    if (this.#isResettingCompositeFilter) return;
    this.#isResettingCompositeFilter = true;
    this.#allSizePagesKey = undefined;
    this.#allSizePagesPromise = undefined;
    this.#sizePagesPrefetchKey = undefined;
    this.#sizePagesPrefetchPromise = undefined;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    history.replaceState({}, '', url);

    try {
      await sectionRenderer.renderSection(this.sectionId, { cache: false, url });
    } catch (error) {
      console.error('[HUSKS size filter] Could not restore collection pagination.', error);
    } finally {
      this.#isResettingCompositeFilter = false;
    }
  }

  #sizeStorageKey() {
    return `husks-composite-size-filter:${window.location.pathname}`;
  }

  #getSelectedSizes() {
    try {
      const value = JSON.parse(sessionStorage.getItem(this.#sizeStorageKey()) ?? '[]');
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item) : [];
    } catch (_error) {
      return [];
    }
  }

  #setSelectedSizes(values) {
    try {
      sessionStorage.setItem(this.#sizeStorageKey(), JSON.stringify(values));
    } catch (_error) {
      // The filter remains usable for the current render when storage is unavailable.
    }
  }

  #syncSizeInputs(values) {
    const selected = new Set(values);
    this.querySelectorAll('[data-husks-size-filter]').forEach((component) => {
      const checkedInputs = [];
      component.querySelectorAll('[data-husks-size-input]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        input.checked = selected.has(input.dataset.husksSizeValue ?? '');
        if (input.checked) checkedInputs.push(input);
      });

      const status = component.closest('details')?.querySelector('facet-status-component');
      if (status && 'updateListSummary' in status && typeof status.updateListSummary === 'function') {
        status.updateListSummary(checkedInputs);
      }

      component.querySelector('.facets__clear')?.classList.toggle('facets__clear--active', checkedInputs.length > 0);
    });
  }

  #migrateNativeSizeFilter() {
    const url = new URL(window.location.href);
    const nativeSizeKeys = [...url.searchParams.keys()].filter((key) => this.#isNativeSizeParameter(key));
    if (nativeSizeKeys.length === 0) return false;

    const values = Array.from(this.querySelectorAll('[data-husks-size-input][data-husks-native-active]'))
      .map((input) => (input instanceof HTMLInputElement ? input.dataset.husksSizeValue : ''))
      .filter(Boolean);
    this.#setSelectedSizes([...new Set(values)]);

    nativeSizeKeys.forEach((key) => url.searchParams.delete(key));
    history.replaceState({}, '', url);
    sectionRenderer.renderSection(this.sectionId, { cache: false });
    return true;
  }

  #isNativeSizeParameter(key) {
    return (
      key === 'filter.v.t.shopify.size' ||
      key.toLowerCase().includes('filter.v.option.maat') ||
      key.toLowerCase().includes('filter.v.option.size')
    );
  }

  async #refreshCompositeSizeFilter() {
    const selectedSizes = this.#getSelectedSizes();
    this.#syncSizeInputs(selectedSizes);

    if (selectedSizes.length > 0) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('page')) {
        url.searchParams.delete('page');
        history.replaceState({}, '', url);
      }
      this.#applyCompositeSizeFilter();
      this.#setCompositeLoading(true);
      const metadata = this.querySelector('[data-composite-size-filter-meta]');
      metadata?.setAttribute('aria-busy', 'true');
      const countElement = metadata?.querySelector('[data-composite-size-count-label]');
      if (countElement) countElement.textContent = '…';

      try {
        await this.#loadAllSizeFilterPages();
      } catch (error) {
        this.#allSizePagesKey = undefined;
        this.#allSizePagesPromise = undefined;
        console.error('[HUSKS size filter] Could not load every collection page.', error);
      } finally {
        metadata?.removeAttribute('aria-busy');
      }
    }

    this.#syncSizeInputs(selectedSizes);
    this.#applyCompositeSizeFilter();
    this.#setCompositeLoading(false);
  }

  #setCompositeLoading(isLoading) {
    this.querySelectorAll('[data-testid="products-count"] [role="status"]').forEach((element) => {
      if (isLoading) {
        element.textContent = '…';
        element.setAttribute('aria-busy', 'true');
      } else {
        element.removeAttribute('aria-busy');
      }
    });
  }

  async #loadAllSizeFilterPages() {
    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    [...url.searchParams.keys()].filter((key) => this.#isNativeSizeParameter(key)).forEach((key) => url.searchParams.delete(key));
    const loadKey = `${url.pathname}?${url.searchParams.toString()}|${lastPage}`;

    if (this.#allSizePagesKey === loadKey) {
      await this.#allSizePagesPromise;
      return;
    }

    this.#allSizePagesKey = loadKey;
    this.#allSizePagesPromise = this.#fetchAndAppendSizePages(url, lastPage, grid);
    await this.#allSizePagesPromise;
  }

  #prefetchCompositeSizePages() {
    const grid = this.querySelector('[data-testid="product-grid"]');
    if (!(grid instanceof HTMLElement)) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    if (lastPage <= 1) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    [...url.searchParams.keys()].filter((key) => this.#isNativeSizeParameter(key)).forEach((key) => url.searchParams.delete(key));
    const prefetchKey = `${url.pathname}?${url.searchParams.toString()}|${lastPage}`;
    if (this.#sizePagesPrefetchKey === prefetchKey) return;

    this.#sizePagesPrefetchKey = prefetchKey;
    const pages = Array.from({ length: lastPage }, (_, index) => index + 1);
    this.#sizePagesPrefetchPromise = Promise.all(
      pages.map((page) => {
        const pageUrl = new URL(url);
        pageUrl.searchParams.set('page', page.toString());
        return sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);
      })
    )
      .then(() => {})
      .catch(() => {
        this.#sizePagesPrefetchKey = undefined;
        this.#sizePagesPrefetchPromise = undefined;
      });
  }

  async #fetchAndAppendSizePages(baseUrl, lastPage, grid) {
    const pages = Array.from({ length: lastPage }, (_, index) => index + 1);

    grid.setAttribute('aria-busy', 'true');
    const pageResults = await Promise.all(
      pages.map(async (page) => {
        const pageUrl = new URL(baseUrl);
        pageUrl.searchParams.set('page', page.toString());
        const html = await sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);
        return { page, html };
      })
    );

    this.#isAppendingSizePages = true;
    grid.querySelectorAll(':scope > [data-product-id]').forEach((card) => card.remove());
    const existingProductIds = new Set();
    for (const { page, html } of pageResults.sort((a, b) => a.page - b.page)) {
      const documentForPage = new DOMParser().parseFromString(html, 'text/html');
      const cards = documentForPage.querySelectorAll('[data-testid="product-grid"] > [data-product-id]');
      cards.forEach((card) => {
        const productId = card.getAttribute('data-product-id');
        if (!productId || existingProductIds.has(productId)) return;
        existingProductIds.add(productId);
        grid.append(card);
      });
    }

    const orderedCards = Array.from(grid.querySelectorAll(':scope > [data-product-id]')).sort(
      (a, b) => Number(a.getAttribute('data-page')) - Number(b.getAttribute('data-page'))
    );
    orderedCards.forEach((card) => grid.append(card));
    this.#isAppendingSizePages = false;
    grid.removeAttribute('aria-busy');
  }

  #applyCompositeSizeFilter() {
    const metadata = this.querySelector('[data-composite-size-filter-meta]');
    if (!(metadata instanceof HTMLElement)) return;

    const selectedSizes = this.#getSelectedSizes();

    let stockCount = 0;
    const matchingCards = [];
    const cards = this.querySelectorAll('[data-husks-size-product]');
    cards.forEach((card) => {
      if (!(card instanceof HTMLElement)) return;

      if (selectedSizes.length === 0) {
        card.hidden = false;
        return;
      }

      const dataElement = card.querySelector('[data-husks-size-data]');
      if (!(dataElement instanceof HTMLScriptElement)) return;

      let data;
      try {
        data = JSON.parse(dataElement.textContent ?? '{}');
      } catch (_error) {
        card.hidden = true;
        return;
      }

      const matchingVariants = data.hasSize
        ? (data.variants ?? []).filter((variant) =>
            selectedSizes.some((selectedSize) => this.#sizeLabelMatches(variant.value, selectedSize))
          )
        : [];
      const matchesSize = data.hasSize === false || matchingVariants.length > 0;
      const matchesStock = data.hasSize === false ? data.productAvailable === true : matchingVariants.some((v) => v.inStock);

      if (matchesSize && matchesStock) stockCount += 1;
      card.hidden = !matchesSize || !matchesStock;
      if (matchesSize && matchesStock) matchingCards.push(card);
    });

    const count = selectedSizes.length === 0 ? Number(metadata.dataset.unfilteredCount) || cards.length : stockCount;
    const grid = this.querySelector('[data-testid="product-grid"]');
    if (grid instanceof HTMLElement) {
      grid.hidden = count === 0;
      if (selectedSizes.length > 0) {
        const pagination = this.querySelector('.pagination');
        if (pagination instanceof HTMLElement) {
          const pageSize = Number(grid.dataset.pageSize) || 24;
          const pageCount = Math.max(1, Math.ceil(stockCount / pageSize));
          this.#compositePage = Math.min(this.#compositePage, pageCount);
          const firstIndex = (this.#compositePage - 1) * pageSize;
          const lastIndex = firstIndex + pageSize;
          matchingCards.forEach((card, index) => {
            card.hidden = index < firstIndex || index >= lastIndex;
          });
          this.#renderCompositePagination(pageCount);
        }
      }
    }

    const emptyState = this.querySelector('[data-composite-size-empty]');
    if (emptyState instanceof HTMLElement) emptyState.hidden = count !== 0;

    const countLabel = this.#formatCountLabel(metadata, count, 'item');
    if (countLabel && selectedSizes.length > 0) {
      const countElement = metadata.querySelector('[data-composite-size-count-label]');
      if (countElement) countElement.textContent = countLabel;

      this.querySelectorAll('[data-testid="products-count"] [role="status"]').forEach((element) => {
        element.textContent = countLabel;
      });
    }

    const seeItemsLabel = this.#formatCountLabel(metadata, count, 'see');
    const seeItemsButton = this.querySelector('.facets__see-results');
    if (seeItemsLabel && seeItemsButton) seeItemsButton.textContent = seeItemsLabel;

    this.querySelectorAll('facets-form-component[data-results-count]').forEach((form) => {
      form.setAttribute('data-results-count', count.toString());
    });
  }

  #renderCompositePagination(pageCount) {
    const nav = this.querySelector('.pagination');
    if (!(nav instanceof HTMLElement)) return;

    const previousLabel =
      nav.dataset.compositePreviousLabel ?? nav.querySelector('.pagination__link--arrow')?.getAttribute('aria-label') ?? 'Vorige';
    const arrows = nav.querySelectorAll('.pagination__link--arrow');
    const nextLabel =
      nav.dataset.compositeNextLabel ?? arrows[arrows.length - 1]?.getAttribute('aria-label') ?? 'Volgende';
    const samplePageLabel = nav.querySelector('.pagination__link--page')?.getAttribute('aria-label') ?? 'Pagina 1';
    const pageLabelTemplate = nav.dataset.compositePageLabel ?? samplePageLabel.replace(/\d+/, '__page__');
    nav.dataset.compositePreviousLabel = previousLabel;
    nav.dataset.compositeNextLabel = nextLabel;
    nav.dataset.compositePageLabel = pageLabelTemplate;
    nav.dataset.compositePageCount = pageCount.toString();
    nav.dataset.current_page = this.#compositePage.toString();

    const list = document.createElement('ul');
    list.className = 'pagination__list';
    list.setAttribute('role', 'list');

    const addArrow = (targetPage, label, symbol, disabled) => {
      const item = document.createElement('li');
      item.className = 'pagination__item';
      const element = document.createElement(disabled ? 'span' : 'a');
      element.className = `pagination__link pagination__link--arrow${disabled ? ' pagination__link--disabled' : ''}`;
      element.setAttribute('aria-label', label);
      element.textContent = symbol;
      if (disabled) {
        element.setAttribute('aria-disabled', 'true');
      } else {
        element.setAttribute('href', `#maat-pagina-${targetPage}`);
        element.dataset.compositeSizePage = targetPage.toString();
      }
      item.append(element);
      list.append(item);
    };

    const addPage = (page) => {
      const item = document.createElement('li');
      item.className = 'pagination__item';
      if (page === this.#compositePage) {
        const current = document.createElement('span');
        current.className = 'pagination__link pagination__link--page pagination__link--current';
        current.setAttribute('aria-current', 'page');
        current.textContent = page.toString();
        item.append(current);
      } else {
        const link = document.createElement('a');
        link.className = 'pagination__link pagination__link--page';
        link.href = `#maat-pagina-${page}`;
        link.dataset.compositeSizePage = page.toString();
        link.setAttribute('aria-label', pageLabelTemplate.replace('__page__', page.toString()));
        link.textContent = page.toString();
        item.append(link);
      }
      list.append(item);
    };

    const addGap = () => {
      const item = document.createElement('li');
      item.className = 'pagination__item pagination__item--gap';
      const gap = document.createElement('span');
      gap.className = 'pagination__link pagination__link--gap';
      gap.setAttribute('aria-hidden', 'true');
      gap.innerHTML = '&hellip;';
      item.append(gap);
      list.append(item);
    };

    addArrow(this.#compositePage - 1, previousLabel, '‹', this.#compositePage === 1);
    const visiblePages = [...new Set([1, this.#compositePage - 1, this.#compositePage, this.#compositePage + 1, pageCount])]
      .filter((page) => page >= 1 && page <= pageCount)
      .sort((a, b) => a - b);
    visiblePages.forEach((page, index) => {
      if (index > 0 && page - visiblePages[index - 1] > 1) addGap();
      addPage(page);
    });
    addArrow(this.#compositePage + 1, nextLabel, '›', this.#compositePage === pageCount);

    nav.replaceChildren(list);
    nav.hidden = pageCount <= 1;
  }

  #formatCountLabel(control, count, type) {
    const one = type === 'see' ? control.dataset.seeItemsOne : control.dataset.itemCountOne;
    const other = type === 'see' ? control.dataset.seeItemsOther : control.dataset.itemCountOther;
    return (count === 1 ? one : other)?.replace('__count__', count.toString()) ?? count.toString();
  }

  #sizeLabelMatches(rawLabel, rawSelectedSize) {
    const label = String(rawLabel ?? '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();
    const selected = String(rawSelectedSize ?? '').toLowerCase().trim();
    if (!label || !selected) return false;

    const selectedNumber = Number(selected.replace(',', '.'));
    if (!Number.isFinite(selectedNumber)) {
      const escaped = selected.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i').test(label);
    }

    const isDimensionOrCapacity = /\d\s*[x×]\s*\d|\b(?:kg|ml|cl|liter|cm)\b/i.test(label);
    if (!isDimensionOrCapacity) {
      const numberPattern = /\d+(?:[.,]\d+)?/g;
      for (const match of label.matchAll(numberPattern)) {
        const value = Number(match[0].replace(',', '.'));
        const suffix = label.slice((match.index ?? 0) + match[0].length).trimStart();
        if (/^(?:m\b|maand|y\b|jaar|kg|ml|cl|cm)/i.test(suffix)) continue;
        if (value === selectedNumber) return true;
      }
    }

    return this.#sizesForAgeLabel(label).has(selectedNumber);
  }

  #sizesForAgeLabel(label) {
    const sizes = new Set();
    if (/\bprematuur\b/i.test(label)) sizes.add(44);
    if (/\bnewborn\b/i.test(label)) {
      sizes.add(44);
      sizes.add(50);
    }

    const addAge = (months) => {
      const pairs = [
        [1, [44, 50]],
        [3, [56, 62]],
        [6, [62, 68]],
        [9, [68, 74]],
        [12, [74, 80]],
        [18, [80, 86]],
        [24, [86, 92]],
        [36, [92, 98]],
        [48, [98, 104]],
      ];
      const match = pairs.find(([maximum]) => months <= maximum) ?? pairs[pairs.length - 1];
      match[1].forEach((size) => sizes.add(size));
    };

    const monthPattern = /(\d+(?:[.,]\d+)?)\s*(?:-|\/|tot|t\/m)?\s*(\d+(?:[.,]\d+)?)?\s*(?:m\b|maand|maanden)/gi;
    for (const match of label.matchAll(monthPattern)) {
      addAge(Number(match[1].replace(',', '.')));
      if (match[2]) addAge(Number(match[2].replace(',', '.')));
    }

    const yearPattern = /(\d+(?:[.,]\d+)?)\s*(?:-|\/|tot|t\/m)?\s*(\d+(?:[.,]\d+)?)?\s*(?:y\b|jaar)/gi;
    for (const match of label.matchAll(yearPattern)) {
      addAge(Number(match[1].replace(',', '.')) * 12);
      if (match[2]) addAge(Number(match[2].replace(',', '.')) * 12);
    }

    return sizes;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
